/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";

// APIs
import searchUIClient from "@/apis/searchUIClient";
import moment from "@/constants/SensolusMoment";

// Utils
import _ from "lodash";
import { DefaultTag } from "@cleanup/components/TagMore";
import {
  bucketTypeConstant,
  supportedGroupTypeConstant,
} from "@/components/SntTableViewCard/SntTableConfigurationConstant";
import { useSelector } from "react-redux";
// languageSelector import removed - not used
import SntText from "@/components/ReactBootstrap/SntText";
import CheckView from "@widgets/views/CheckView";
import { useOrg } from "@/contexts/OrgContext";
import SntLink from "@/components/ReactBootstrap/SntLink";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface InitialState {
  originalCols?: any[];
  extraCols?: any[];
  includeDynamicColumn?: boolean;
}

export const useColumnDefinition = (
  queryObjectType: string | null,
  categories: string[] | undefined | null,
  preventCols?: string[],
  initialState: InitialState = {},
  channelId: string | null = null
) => {
  const {
    originalCols = [],
    extraCols = [],
    includeDynamicColumn = false,
  } = initialState;

  const [allColumnDefs, setAllColumnDefs] = useState<any[] | null>(null);

  const [filterDataServer, setFilterDataServer] = useState<any[] | null>(null);
  const serverColumnDef = useRef<any>(null);
  // language variable removed - not used
  const loginInfo = useSelector((state: any) => state.user);
  const { orgId } = useOrg();
  

  // Get server column definition
  useEffect(() => {
    if (!queryObjectType) return setFilterDataServer([]);

    let isMounted = true;

    let _categoriesParam = null;
    if (categories) {
      _categoriesParam = { categories };
    }

    const orgParam = orgId ? `?org=${orgId}` : "";
    searchUIClient
      .getFilterColumns(queryObjectType, _categoriesParam, orgParam, channelId)
      .then(({ data }) => {
        if (isMounted) setFilterDataServer(data);
      })
      .catch((_error) => {
        if (isMounted) setFilterDataServer([]);
      });

    return () => {
      isMounted = false;
    };
  }, [queryObjectType, categories, orgId, channelId]);

  useEffect(() => {
    const columns = dynamicUpdateRenderDescriptorCols();
    setAllColumnDefs(columns || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preventCols, filterDataServer, includeDynamicColumn]);

  const dynamicUpdateRenderDescriptorCols = () => {
    if (!filterDataServer) return;
    const _allColumns: Record<string, any> = {};
    originalCols.forEach((obj: any) => {
      _allColumns[obj.key] = {
        columnKey: obj.key,
        title: obj.title,
        Cell: obj.Cell,
        style: obj.style,
        supportKibanaKey: obj.supportKibanaKey,
        cellCopyData: obj.cellCopyData,
        cellFilterValue: obj.cellFilterValue,
      };
    });
    const _map: Record<string, any> = {};
    filterDataServer.forEach((colDataServer: any, index: number) => {
      const _key = colDataServer.columnKey; //table description key
      if (
        includeDynamicColumn === false &&
        colDataServer.columnType === "DYNAMIC"
      ) {
        return;
      }

      const mapObj = _allColumns[_key] || {};

      mapObj.title = mapObj.title || colDataServer.title || _key;
      mapObj.style = mapObj.style || {};

      // calculate width when no provide width style
      if (!mapObj.style.width) {
        const _size = mapObj.title.length * 7 + 15;
        let width = 100;
        if (_size > 100) {
          if (_size <= 150) {
            width = 150;
          } else if (_size <= 200) {
            width = 200;
          } else if (_size <= 250) {
            width = 250;
          } else if (_size <= 300) {
            width = 300;
          } else if (_size <= 350) {
            width = 350;
          } else if (_size <= 400) {
            width = 400;
          } else {
            width = 600;
          }
        }
        mapObj.style.width = width;
      }

      colDataServer.settingTitle = mapObj.settingTitle;
      mapObj.columnKey = _key;
      let aggregateColumnKey = _key;

      if (
        colDataServer.aggregateColumns &&
        colDataServer.aggregateColumns.length > 0
      ) {
        const aggregateColumn = colDataServer.aggregateColumns[0];
        aggregateColumnKey = aggregateColumn.alias || aggregateColumn.column;
        mapObj.supportedGroupType = colDataServer.supportedGroupType;
        if (
          colDataServer.supportedGroupType ===
          supportedGroupTypeConstant.NUMERIC_BUCKET
        ) {
          mapObj.supportedAggregateMetrics = ["COUNT", "MIN", "MAX", "AVG", "SUM"];
        } else if (
          colDataServer.supportedGroupType ===
          supportedGroupTypeConstant.DATE_BUCKET
        ) {
          mapObj.supportedAggregateMetrics = ["COUNT", "MIN", "MAX"];
        } else {
          mapObj.supportedAggregateMetrics = ["COUNT"];
        }
        mapObj.supportedGroupUnits = colDataServer.supportedGroupUnits;

        if (aggregateColumn.durationUnit) {
          mapObj.durationUnit = aggregateColumn.durationUnit;
        }
      }
      mapObj.aggregateColumn = aggregateColumnKey;
      mapObj.aliasName =
        colDataServer.redirectColumn?.alias || aggregateColumnKey;
      mapObj.redirectFilter = colDataServer.redirectFilter;
      mapObj.redirectColumn = colDataServer.redirectColumn;

      mapObj.disabledGroupColumn = colDataServer.disabledGroupColumn;

      mapObj.filterKeys = colDataServer.filterKeys;

      // put BE column defined to tableDescriptor
      mapObj.tableDescriptor = colDataServer;

      // if (colData.sortProperty) {
      //   mapObj.orderable = true;
      // }

      // if (colData.columnType === "DYNAMIC" && colData.sortProperty && colData.sortProperty.enums) {
      //   mapObj.Cell = ({ cell, value }) => {
      //     let full = cell.row.original;
      //     let enumVal = full[_key] || "";
      //     return <DefaultTag tagName={enumVal} />;
      //   };
      // }

      _map[_key] = mapObj;
    });
    serverColumnDef.current = _map;

    const _mapCols = JSON.parse(JSON.stringify(_map));
    const keys = Object.keys(_mapCols);

    if (keys.length > 0) {
      // options column from FE definition, need to overright BE definition
      const allRawColsByKeys = _.keyBy(originalCols, (o: any) => o.key);

      const _allCols = keys
        .map((key) => {
          if (
            preventCols &&
            preventCols.length &&
            preventCols.indexOf(key) !== -1
          )
            return null;
          const colObj = _mapCols[key];
          const { tableDescriptor } = colObj;

          const obj: any = {
            key: key,
            category: tableDescriptor.category,
            categoryId: tableDescriptor.categoryId || tableDescriptor.category,
            description: tableDescriptor.description,
            aggregateColumn: colObj.aggregateColumn,
            aliasName: colObj.aliasName,
            redirectFilter: colObj.redirectFilter,
            redirectColumn: colObj.redirectColumn,
            supportKibanaKey: colObj.supportKibanaKey,
            supportedGroupType: tableDescriptor.supportedGroupType,
            supportedAggregateMetrics: colObj.supportedAggregateMetrics,
            supportedGroupUnits: tableDescriptor.supportedGroupUnits,
            durationUnit: colObj.durationUnit,
            filterKeys: colObj.filterKeys,
            disabledGroupColumn: colObj.disabledGroupColumn,
            style: colObj.style || {},
            title: colObj.title,
            tableDescriptor: tableDescriptor || {},
            // sortedIndex alway -1. because the orderIndex is already removed in backend
            sortedIndex:
              tableDescriptor && Number.isInteger(tableDescriptor.orderIndex)
                ? tableDescriptor.orderIndex
                : -1,
          };

          // if column is not defined by originalCols, use dynamic render
          if (allRawColsByKeys[key]) {
            obj.title = obj.title || allRawColsByKeys[key].title;
            obj.cellDefineRender = allRawColsByKeys[key].Cell;
            obj.cellCopyData = allRawColsByKeys[key].cellCopyData;
            obj.cellFilterValue = allRawColsByKeys[key].cellFilterValue;
            obj.style = colObj.style || allRawColsByKeys[key].style;
            obj.disableSortBy = allRawColsByKeys[key].disableSortBy;
            obj.cellClass = allRawColsByKeys[key].cellClass;
          }

          //For dynamic column
          if (!obj.cellDefineRender) {
            obj.cellDefineRender = ({ cell, value }: any) => {
              if (typeof value === "boolean") {
                return renderCheckView(value);
              }
              if (obj.categoryId === "processDefinition") {
                return renderProcess(cell.column, cell.row, value);
              }
              return (
                renderDynamicValue(cell.column, value, cell) || value || ""
              );
            };
          }

          // eslint-disable-next-line react/display-name
          obj.Cell = (data: { cell: { column: unknown; row: unknown }; value: unknown }) => obj.cellDefineRender(data) ?? <></>;

          // for DATE_BUCKET type or Cell is not defined, we use dynamic rendering
          if (
            [supportedGroupTypeConstant.DATE_BUCKET].includes(
              obj.supportedGroupType
            )
          ) {
            const origCell = obj.Cell;
            // dynamic rendering when grouped, original when not grouped
            obj.Cell = ({ cell, value }: any) => {
              if (cell.column.isGrouped) {
                const cellValue =
                  value || cell.row.original[cell.column.aliasName];
                return (
                  renderDynamicValue(cell.column, cellValue, cell) ||
                  cellValue ||
                  ""
                );
              } else {
                return origCell({ cell, value });
              }
            };
          }

          return obj;
        })
        .filter((col) => col);
      return [..._allCols, ...(extraCols || [])];
    }
    return [...originalCols, ...(extraCols || [])];
  };

  const renderDynamicValue = (column: any, value: any, cell: any) => {
    const tableDescriptor = column.tableDescriptor;
    if (!tableDescriptor) {
      return <SntText value={value} />;
    }
    const isGrouped = column.isGrouped;
    const bucketType = column.bucketType;
    const supportedGroupType = tableDescriptor.supportedGroupType;
    const format =
      tableDescriptor.sortProperties &&
      tableDescriptor.sortProperties.length > 0 &&
      tableDescriptor.sortProperties[0]
        ? tableDescriptor.sortProperties[0].format
        : null;
    if (
      value &&
      tableDescriptor.sortProperties &&
      tableDescriptor.sortProperties.length > 0 &&
      !_.isEmpty(tableDescriptor.sortProperties[0].enumI18s)
    ) {
      const enumI18s = tableDescriptor.sortProperties[0].enumI18s;
      const enums = tableDescriptor.sortProperties[0].enums;
      const index = enums.indexOf(value);
      const _value =
        loginInfo &&
        enumI18s[loginInfo.language] &&
        enumI18s[loginInfo.language][index]
          ? enumI18s[loginInfo.language][index]
          : undefined;
      return _value ? <DefaultTag tagName={_value} /> : value;
    }

    if (!_.isNull(value) && !_.isUndefined(value)) {
      if (supportedGroupType === supportedGroupTypeConstant.DATE_BUCKET) {
        if (isGrouped) {
          // For grouped date buckets, parse in UTC to avoid timezone shift
          // Backend sends dates like "2025-10-01T00:00:00.000Z" which should display as "Oct 2025"
          if (bucketType === bucketTypeConstant.YEAR) {
            return moment.utc(value).format("yyyy");
          } else if (bucketType === bucketTypeConstant.MONTH) {
            return moment.utc(value).format("MMM yyyy");
          } else if (
            bucketType === bucketTypeConstant.DAY ||
            bucketType === bucketTypeConstant.WEEK
          ) {
            return moment.utc(value).format("MMM DD yyyy");
          }
        }
        return moment(value).format("lll");
      }
    } else {
      return <>{translate(LanguageKey.na_key)}</>;
    }

    if (format === "URL") {
      const linkInfo = value.match(/\(([^)]+)\)/)
        ? value.match(/\(([^)]+)\)/)[1]
        : "";
      let tooltip = "";
      let link = value;
      if (linkInfo) {
        tooltip = linkInfo.match(/"([^)]+)"/)
          ? linkInfo.match(/"([^)]+)"/)[1]
          : "";
        if (tooltip) {
          link = linkInfo.substring(0, linkInfo.indexOf(" "));
        } else {
          link = linkInfo;
        }
      }
      const linkLabel = value.match(/\[([^)]+)\]/)
        ? value.match(/\[([^)]+)\]/)[1]
        : "";
      return (
        <a target={"_blank"} rel={"noreferrer"} title={tooltip} href={link}>
          {linkLabel || link}
        </a>
      );
    } else if (format === "BASE64_IMAGE") {
      // Tables backed by THIRD_PARTY_DATA_RECORD (and similar) expose `deviceId`
      // separately from `id` (which is the record id). Fall back to `id` for
      // legacy callers where the row IS the device.
      const deviceIdForUrl = cell.row.original.deviceId ?? cell.row.original.id;
      return (
        <img
          src={"/rest/channels/image/" + deviceIdForUrl + "/" + value}
          style={{
            borderRadius: 4,
            width: 80,
            height: 80,
          }}
        />
      );
    } else if (format === "BASE64_PDF") {
      const isUUID = value.length === 36;
      if (isUUID) {
        const deviceIdForUrl = cell.row.original.deviceId ?? cell.row.original.id;
        return (
          <a href={`/rest/channels/image/${deviceIdForUrl}/${value}`}>
            {translate(LanguageKey.hc_downloadPDF_key)}
          </a>
        );
      }
      const downloadPdf = (e: any) => {
        e.preventDefault();
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${value}`;
        link.download = `document_${cell.row.original.id}.pdf`;
        link.click();
      };
      return (
        <SntLink onClick={downloadPdf}>{translate(LanguageKey.hc_downloadPDF_key)}</SntLink>
      );
    }

    return <SntText value={value} />;
  };

  const renderProcess = (column: any, row: any, value: any) => {
    return <SntText value={row.original[column.key + "_stateLabel"]}></SntText>;
  };

  const renderCheckView = (value: any) => {
    return <CheckView value={value} />;
  };

  const newFilterKeys = useRef<string[]>([]);
  const sortedFilterKeys = useRef<string[]>([]);

  const getNewCols = (dataOptions: any) => {
    const currentFilterKeys =
      dataOptions.optionsSettings && dataOptions.optionsSettings.query
        ? dataOptions.optionsSettings.query.map((q: any) => q.filterKey).sort()
        : [];
    newFilterKeys.current = [];
    if (
      JSON.stringify(sortedFilterKeys.current) !==
      JSON.stringify(currentFilterKeys)
    ) {
      currentFilterKeys.forEach((key: any) => {
        if (sortedFilterKeys.current.indexOf(key) === -1) {
          newFilterKeys.current.push(key);
        }
      });

      sortedFilterKeys.current = currentFilterKeys;
    }

    const newColumns: any[] = [];

    // add new columns from new filter keys
    newFilterKeys.current.forEach((filterKey) => {
      for (const key in serverColumnDef.current) {
        if (serverColumnDef.current[key].filterKeys.indexOf(filterKey) !== -1) {
          newColumns.push(key);
          continue;
        }
      }
    });

    return newColumns;
  };

  if (!allColumnDefs) return {};
  return {
    allColumnDefs,
    getNewCols,
    serverColumnDef: serverColumnDef.current,
  };
};
